import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

// Utility functions for the application

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Calculates the Levenshtein distance between two strings
 * Used for fuzzy string matching to detect typos
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  const matrix: number[][] = [];
  
  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  
  return matrix[s2.length][s1.length];
}

/**
 * Calculates similarity score between two strings (0-100)
 * 100 = identical, 0 = completely different
 */
export function calculateSimilarityScore(str1: string, str2: string): number {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  
  if (maxLength === 0) return 100;
  
  const similarity = ((maxLength - distance) / maxLength) * 100;
  return Math.round(similarity);
}

/**
 * Extrahiert ALLE Zutaten aus verschachtelter JSONB-Struktur und gibt sie kommagetrennt zurück
 * Unterstützt: Arrays, Objekte mit hauptzutaten/zutaten/allergene/zusatzstoffe, Strings
 * 
 * @param zutaten - JSONB-Objekt aus Datenbank (speisen/getraenke)
 * @returns Kommagetrennte Liste aller Zutaten
 */
export function extractIngredientsFromJsonb(zutaten: any): string {
  if (!zutaten) return '';
  
  const ingredients = new Set<string>(); // Set für automatische Duplikat-Entfernung
  
  // Rekursive Extraktionsfunktion
  const extract = (item: any, depth: number = 0): void => {
    if (!item) return;
    
    // String direkt hinzufügen
    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (trimmed.length > 0) {
        ingredients.add(trimmed);
      }
      return;
    }
    
    // Array verarbeiten
    if (Array.isArray(item)) {
      item.forEach(subItem => extract(subItem, depth + 1));
      return;
    }
    
    // Objekt verarbeiten
    if (typeof item === 'object') {
      // 1. Name/Klassenname hinzufügen
      if (item.name && typeof item.name === 'string') {
        const trimmed = item.name.trim();
        if (trimmed.length > 0) {
          ingredients.add(trimmed);
        }
      }
      if (item.klassenname && typeof item.klassenname === 'string' && item.klassenname !== item.name) {
        const trimmed = item.klassenname.trim();
        if (trimmed.length > 0) {
          ingredients.add(trimmed);
        }
      }
      
      // 2. Hauptzutaten rekursiv
      if (item.hauptzutaten) extract(item.hauptzutaten, depth + 1);
      
      // 3. Sub-Zutaten rekursiv (WICHTIG: Nur wenn Array nicht leer ist)
      // Falls zutaten leer ist [], wurde der Name bereits oben hinzugefügt (Blatt-Knoten)
      if (item.zutaten && Array.isArray(item.zutaten) && item.zutaten.length > 0) {
        extract(item.zutaten, depth + 1);
      }
      
      // 4. Allergene
      if (item.allergene) {
        if (Array.isArray(item.allergene)) {
          item.allergene.forEach((allergen: any) => {
            if (typeof allergen === 'string') {
              const trimmed = allergen.trim();
              if (trimmed.length > 0) {
                ingredients.add(trimmed);
              }
            } else if (allergen && allergen.name) {
              const trimmed = allergen.name.trim();
              if (trimmed.length > 0) {
                ingredients.add(trimmed);
              }
            }
          });
        }
      }
      
      // 5. Zusatzstoffe (inkl. E-Nummern)
      if (item.zusatzstoffe) {
        if (Array.isArray(item.zusatzstoffe)) {
          item.zusatzstoffe.forEach((additive: any) => {
            if (typeof additive === 'string') {
              const trimmed = additive.trim();
              if (trimmed.length > 0) {
                ingredients.add(trimmed);
              }
            } else if (additive && additive.name) {
              const trimmed = additive.name.trim();
              if (trimmed.length > 0) {
                ingredients.add(trimmed);
              }
              if (additive.e_nummer) {
                const trimmed = additive.e_nummer.trim();
                if (trimmed.length > 0) {
                  ingredients.add(trimmed);
                }
              }
            }
          });
        }
      }
    }
  };
  
  extract(zutaten);
  
  // Als kommagetrennte Liste zurückgeben
  return Array.from(ingredients).join(', ');
}

/**
 * Rendert Zutaten einheitlich als kommagetrennte Liste
 * Unterstützt sowohl Array als auch String-Format
 */
export function renderIngredients(ingredients: string | string[] | undefined): string {
  if (!ingredients) return '';
  if (typeof ingredients === 'string') return ingredients;
  if (Array.isArray(ingredients)) return ingredients.join(', ');
  return '';
}

/**
 * Centralized function to format ingredients with nested structures in brackets
 * Example: { hauptzutaten: [{ name: "Butter", zutaten: [{ name: "Milch" }] }] }
 * becomes: "Butter (Milch)"
 * 
 * This replaces the duplicated formatIngredientsWithBrackets functions in:
 * - DetailedAnalysisTable.tsx
 * - ingredientHighlighting.ts  
 * - pdfReportService.ts
 */
/**
 * Dedupliziert kommagetrennte Zutatenlisten:
 * Entfernt einfache Namen wenn sie bereits als Hauptzutat mit Klammern vorkommen.
 * z.B. "Grillhaxe (Schweinehaxe, ...), Bayrisch Kraut (...), Bayrisch Kraut" 
 * → "Grillhaxe (Schweinehaxe, ...), Bayrisch Kraut (...)"
 */
function deduplicateCommaSeparatedIngredients(text: string, productName?: string): string {
  // Splitte nach Komma, aber respektiere Klammern (nicht innerhalb von Klammern splitten)
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    
    if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) parts.push(current.trim());
  
  // Sammle alle Hauptnamen (Teil vor der Klammer) aus Einträgen MIT Klammern
  const mainNamesWithBrackets = new Set<string>();
  parts.forEach(part => {
    if (part.includes('(')) {
      const mainName = part.split('(')[0].trim().toLowerCase();
      if (mainName) mainNamesWithBrackets.add(mainName);
    }
  });
  
  // ✅ Produktnamen-Filterung (nur exakte Gleichheit, keine Teilwort-Entfernung)
  const productNameLower = productName ? productName.toLowerCase().trim() : '';
  
  // Sammle alle Zutatenwörter die bereits irgendwo in der Liste vorkommen (für Endfilterung)
  const allIngredientsLower = parts.map(p => p.toLowerCase().trim());
  
  // Filtere Duplikate
  const seen = new Set<string>();
  const deduped = parts.filter((part, idx) => {
    const partLower = part.toLowerCase().trim();
    if (!partLower) return false;
    
    // Exaktes Duplikat?
    if (seen.has(partLower)) return false;
    seen.add(partLower);
    
    // ✅ Produktnamen-Filterung
    if (!part.includes('(') && productNameLower) {
      // Exakter vollständiger Name
      if (partLower === productNameLower) return false;
      
    }
    
    // Einfacher Name (ohne Klammern) der schon als Hauptzutat mit Klammern existiert?
    if (!part.includes('(')) {
      // Prüfe ob dieser Name dem Hauptnamen eines Klammer-Eintrags entspricht
      if (mainNamesWithBrackets.has(partLower)) return false;
      
      // Prüfe auch teilweise Übereinstimmung: "Grillhaxe in Malzbiersoße" matched "Grillhaxe"
      for (const mainName of mainNamesWithBrackets) {
        if (partLower.includes(mainName) || mainName.includes(partLower)) {
          return false;
        }
      }
    }
    
    return true;
  });
  
  return deduped.join(', ');
}

export function formatIngredientsWithBrackets(
  ingredients: string | string[] | Record<string, any>, 
  rawIngredients?: Record<string, any> | string,
  productName?: string
): string {
  // Performance: Debug logs entfernt

  // Helper to ensure we have a parsed object
  const ensureParsed = (input: any): any => {
    if (!input) return null;
    
    // Already an object with hauptzutaten/zutaten - use directly
    if (typeof input === 'object' && input !== null) {
      return input;
    }
    
    // String - try to parse as JSON
    if (typeof input === 'string') {
      const trimmed = input.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
          (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return null;
        }
      }
      // Plain string - return null so we can use it as fallback
      return null;
    }
    
    return null;
  };

  // Try rawIngredients first (highest priority)
  let source = ensureParsed(rawIngredients);
  
  // Try ingredients if rawIngredients didn't work
  if (!source) {
    source = ensureParsed(ingredients);
  }
  
  // If still no parsed source, check if ingredients is a plain string
  if (!source) {
    if (typeof ingredients === 'string' && ingredients.trim().length > 0) {
      const trimmed = ingredients.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        // ✅ Deduplizierung auch für plain Strings: Entferne Hauptzutaten-Duplikate
        return deduplicateCommaSeparatedIngredients(trimmed, productName);
      }
    }
    return '';
  }

  // If array of strings, join with comma
  if (Array.isArray(source) && source.every((i: any) => typeof i === 'string')) {
    return source.join(', ');
  }

  // Recursive function for nested structures - NUR Zutaten extrahieren
  const formatIngredient = (ingredient: any, depth: number = 0): string => {
    if (!ingredient || depth > 15) return ''; // Prevent infinite recursion
    
    // If string, return directly
    if (typeof ingredient === 'string') {
      return ingredient.trim();
    }

    if (typeof ingredient === 'object' && ingredient !== null) {
      const name = (ingredient.name || ingredient.zutat || '').trim();
      
      // Check for sub-ingredients - EXPLIZIT nur zutaten/ingredients/bestandteile, NICHT allergene/zusatzstoffe
      const subIngredients = ingredient.zutaten || ingredient.ingredients || ingredient.bestandteile;
      
      // NUR verarbeiten wenn es ein Array mit Inhalt ist
      if (Array.isArray(subIngredients) && subIngredients.length > 0) {
        // Filter: Nur Objekte mit name/zutat Property oder Strings verarbeiten
        const validSubs = subIngredients.filter((sub: any) => 
          (typeof sub === 'string' && sub.trim().length > 0) ||
          (typeof sub === 'object' && sub !== null && (sub.name || sub.zutat))
        );
        
        if (validSubs.length > 0) {
          const formattedSubs = validSubs
            .map((sub: any) => formatIngredient(sub, depth + 1))
            .filter((s: string) => s.length > 0);
          
          if (formattedSubs.length > 0) {
            if (!name) return formattedSubs.join(', ');
            
            // ✅ FIX: Keine redundanten Klammern wenn einzige Sub-Zutat identisch mit Hauptzutat ist
            // z.B. "Garnelen (Garnelen)" → "Garnelen", "Hähnchenbrustfilet (Hähnchenbrustfilet)" → "Hähnchenbrustfilet"
            if (formattedSubs.length === 1 && formattedSubs[0].toLowerCase().trim() === name.toLowerCase().trim()) {
              return name;
            }
            
            return `${name} (${formattedSubs.join(', ')})`;
          }
        }
      }

      // No valid sub-ingredients - just return the name
      return name;
    }

    return '';
  };

  // Extract main ingredients (top-level)
  let mainIngredients: any[] = [];

  if (Array.isArray(source)) {
    mainIngredients = source;
  } else if (source?.hauptzutaten && Array.isArray(source.hauptzutaten)) {
    mainIngredients = source.hauptzutaten;
  } else if (source?.ingredients && Array.isArray(source.ingredients)) {
    mainIngredients = source.ingredients;
  } else if (source?.zutaten && Array.isArray(source.zutaten)) {
    mainIngredients = source.zutaten;
  } else if (typeof source === 'object' && source !== null) {
    // Fallback: Wenn das Object direkt ein einzelnes Zutat-Objekt ist (mit name/zutat)
    if (source.name || source.zutat) {
      const formatted = formatIngredient(source);
      if (formatted) return formatted;
    }
    
    // Letzter Fallback: Versuche alle Werte zu sammeln die keine Metadaten sind
    const excludeKeys = ['allergene', 'zusatzstoffe', 'additives', 'allergens', 'kategorie', 'category', 'id', 'name'];
    const potentialIngredients: any[] = [];
    
    for (const [key, value] of Object.entries(source)) {
      if (excludeKeys.includes(key.toLowerCase())) continue;
      
      if (Array.isArray(value)) {
        potentialIngredients.push(...value);
      } else if (typeof value === 'object' && value !== null) {
        potentialIngredients.push(value);
      }
    }
    
    if (potentialIngredients.length > 0) {
      mainIngredients = potentialIngredients;
    }
  }

  // Format each main ingredient and join with commas
  const formatted = mainIngredients.map(ing => formatIngredient(ing)).filter(Boolean);
  
  // ✅ Deduplizierung: Entferne Einträge die bereits als Teil einer anderen Zutat vorkommen
  // z.B. wenn "Obatzda (Butter, Camembert, ...)" UND "Obatzda" separat existieren
  
  // ✅ Produktnamen-Filterung (nur exakte Gleichheit, keine Teilwort-Entfernung)
  const productNameLower2 = productName ? productName.toLowerCase().trim() : '';
  
  // ✅ Sammle alle Hauptnamen die bereits mit Klammern (Detail-Zutaten) vorkommen
  const mainNamesWithDetails = new Set<string>();
  formatted.forEach(entry => {
    if (entry.includes('(')) {
      mainNamesWithDetails.add(entry.toLowerCase().split('(')[0].trim());
    }
  });
  
  const deduped = formatted.filter((entry, idx) => {
    const entryLower = entry.toLowerCase().trim();
    if (!entryLower) return false;
    
    // ✅ Entferne wenn: exakt Produktname, Teilwort des Produktnamens, oder bereits als Hauptzutat mit Klammern vorhanden
    if (!entry.includes('(')) {
      if (productNameLower2 && entryLower === productNameLower2) return false;
      
      // Entferne wenn der Eintrag bereits als Hauptzutat MIT Klammern existiert
      if (mainNamesWithDetails.has(entryLower)) return false;

      // ✅ FIX: Entferne standalone Einträge die semantisch redundant sind
      // z.B. "Spiegelei" wenn "Ei" schon in Klammern einer anderen Zutat steht
      // z.B. "Schweine-Mini-Haxe" wenn "Schweinehaxe (...)" bereits existiert
      const entryNormalized = entryLower.replace(/[\-\s]/g, '');
      
      for (const [otherIdx, other] of formatted.entries()) {
        if (otherIdx === idx) continue;
        const otherLower = other.toLowerCase().trim();
        
        // Fall A: Standalone-Eintrag ist in einem Hauptnamen mit Klammern enthalten
        // z.B. "Schweine-Mini-Haxe" → "schweineminihaxe" ist ähnlich zu "schweinehaxe"
        if (other.includes('(')) {
          const otherMainName = otherLower.split('(')[0].trim();
          const otherMainNormalized = otherMainName.replace(/[\-\s]/g, '');
          
          // Exakter Match oder einer enthält den anderen (nach Normalisierung)
          if (otherMainNormalized === entryNormalized) return false;
          if (entryNormalized.length >= 5 && otherMainNormalized.includes(entryNormalized)) return false;
          if (otherMainNormalized.length >= 5 && entryNormalized.includes(otherMainNormalized)) return false;
        }
        
        // Fall B: Standalone-Eintrag ist bereits als Sub-Zutat in Klammern aufgeführt
        // z.B. "Spiegelei" enthält "ei" - aber prüfe ob der Eintrag selbst
        // bereits als Sub-Zutat in einer Klammer vorkommt
        if (other.includes('(') && other.includes(')')) {
          const bracketContent = other.substring(other.indexOf('(') + 1, other.lastIndexOf(')'));
          const subParts = bracketContent.split(',').map(s => s.trim().toLowerCase());
          
          // Prüfe ob der standalone Eintrag exakt als Sub-Zutat vorkommt
          if (subParts.some(sub => sub === entryLower)) return false;
          
          // Prüfe ob der standalone Eintrag eine Variante einer Sub-Zutat ist
          // z.B. "Spiegelei" enthält "Ei" als Bestandteil UND "Ei" ist Sub-Zutat
          if (subParts.some(sub => {
            if (sub.length < 2) return false;
            const subNorm = sub.replace(/[\-\s]/g, '');
            return entryNormalized.includes(subNorm) && subNorm.length >= 2 && entryNormalized !== subNorm;
          })) {
            // Zusätzlicher Check: Der standalone Eintrag muss wirklich ein Kompositum sein
            // "Spiegelei" enthält "ei" → redundant
            // Aber "Sellerie" enthält "ei" → NICHT redundant (anderes Wort)
            // Prüfe: Beginnt oder endet der Eintrag mit der Sub-Zutat?
            const matchingSub = subParts.find(sub => {
              const subNorm = sub.replace(/[\-\s]/g, '');
              return subNorm.length >= 2 && entryNormalized.endsWith(subNorm);
            });
            if (matchingSub) return false;
          }
        }
      }
    }
    
    // Fall 1: Einfacher Eintrag (ohne Klammern) - prüfe ob er als Hauptzutat MIT Klammern vorkommt
    if (!entry.includes('(')) {
      const isDuplicate = formatted.some((other, otherIdx) => {
        if (otherIdx === idx) return false;
        const otherLower = other.toLowerCase().trim();
        
        if (other.includes('(')) {
          const otherMainName = otherLower.split('(')[0].trim();
          if (otherMainName === entryLower) return true;
        }
        
        return false;
      });
      if (isDuplicate) return false;
    }
    
    // Fall 2: Eintrag MIT Klammern - prüfe ob ein anderer Eintrag denselben Hauptnamen hat
    if (entry.includes('(')) {
      const mainName = entryLower.split('(')[0].trim();
      const earlierDuplicate = formatted.findIndex((other, otherIdx) => {
        if (otherIdx >= idx) return false;
        const otherLower = other.toLowerCase().trim();
        if (other.includes('(')) {
          return otherLower.split('(')[0].trim() === mainName;
        }
        return false;
      });
      if (earlierDuplicate !== -1) return false;
    }
    
    // Fall 3: Prüfe ob exakt derselbe Eintrag schon vorher in der Liste steht
    const firstOccurrence = formatted.findIndex(
      (other) => other.toLowerCase().trim() === entryLower
    );
    if (firstOccurrence !== idx) return false;
    
    return true;
  });
  
  // ✅ SAFEGUARD: Wenn Dedup ALLE Einträge entfernt hat, behalte die Original-Liste
  // z.B. "Wasser 0,2l" mit Zutat "Wasser" - darf nicht komplett leer werden
  const finalList = deduped.length > 0 ? deduped : formatted;
  
  // Use comma separator for main ingredients
  if (finalList.length > 0) {
    return finalList.join(', ');
  }
  
  // Absoluter Fallback für unbekannte Strukturen: Versuche als String darzustellen
  if (typeof source === 'object') {
    // Wenn es ein Object ist aber wir nichts extrahieren konnten, gib leeren String zurück
    // statt den JSON-String zu zeigen
    return '';
  }
  
  return String(source);
}

/**
 * Formatiert gesetzliche Grundlagen so, dass EU: und D: jeweils am Anfang einer neuen Zeile stehen
 * @param text - Der zu formatierende Text
 * @returns Formatierter Text mit Zeilenumbrüchen vor EU: und D:
 */
export function formatLegalBasis(text: string | undefined): string {
  if (!text) return '';
  
  let formatted = text
    // Entferne existierende Newlines vor EU: und D: um Duplikate zu vermeiden
    .replace(/\n+\s*(EU:|D:)/g, ' $1')
    // Füge Newline vor EU: hinzu (außer am Anfang)
    .replace(/([^\n])\s*(EU:)/g, '$1\n$2')
    // Füge Newline vor D: hinzu (außer am Anfang)
    .replace(/([^\n])\s*(D:)/g, '$1\n$2')
    // Bereinige mehrfache Newlines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  
  return formatted;
}

/**
 * Formatiert user_number als 5-stellige Nummer
 */
export function formatUserNumber(userNumber: number | null | undefined): string {
  if (!userNumber) return '00000';
  return String(userNumber).padStart(5, '0');
}

/**
 * Formatiert scan_number als 4-stellige Nummer
 */
export function formatScanNumber(scanNumber: number | null | undefined): string {
  if (!scanNumber) return '0000';
  return String(scanNumber).padStart(4, '0');
}

/**
 * Extrahiere Package-Initialen für ID-Format
 * S, M, L, XL -> S, M, L, XL
 */
export function getPackageInitials(packageCode: string | null | undefined): string {
  if (!packageCode) return 'S';
  const normalized = packageCode.toUpperCase().trim();
  if (['S', 'M', 'L', 'XL'].includes(normalized)) return normalized;
  // Handle "basic" package
  if (normalized === 'BASIC' || normalized.includes('BASIC')) return 'B';
  // Fallback für package-s, package-m etc.
  if (normalized.includes('XL')) return 'XL';
  if (normalized.includes('L')) return 'L';
  if (normalized.includes('M')) return 'M';
  if (normalized.includes('S')) return 'S';
  return 'S';
}

/**
 * Typ-Präfixe für Dokument-IDs
 * SC = Scan, PB = Prüfbericht, HE = Handlungsempfehlungen, 
 * AZL = Allergen-/Zusatzstoffliste, UK = Überarbeitete Karte, RE = Rechnung
 */
export type DocumentIdType = 'SC' | 'PB' | 'AB' | 'HE' | 'AZL' | 'UK' | 'RE' | 'ED' | 'KA' | 'TA' | 'WA' | 'MA' | 'BL' | 'QR' | 'LI' | 'KB';

/**
 * Alias für Abwärtskompatibilität
 */
export type ReportIdType = 'PB' | 'HE' | 'AZL' | 'UK' | 'RE';

/**
 * NEUE zentrale Funktion zur Generierung einheitlicher Dokument-IDs
 * Format: TYP-UUUUU-SSSS-JJ-P
 * Beispiel: PB-00123-0001-25-M
 * 
 * @param type - Dokumenttyp (SC, PB, HE, AZL, UK, RE)
 * @param userNumber - 5-stellige User-Nummer aus profiles.user_number
 * @param scanNumber - 4-stellige Scan-Nummer aus user_artikelscans.scan_number
 * @param packageCode - Paket-Code (S, M, L, XL)
 */
export function generateDocumentId(
  type: DocumentIdType,
  userNumber: number | null | undefined,
  scanNumber: number | null | undefined,
  packageCode?: string | null
): string {
  const year = new Date().getFullYear().toString().slice(-2);
  const userPart = formatUserNumber(userNumber);
  const scanPart = formatScanNumber(scanNumber);
  const pkgInitial = getPackageInitials(packageCode);
  
  return `${type}-${userPart}-${scanPart}-${year}-${pkgInitial}`;
}


/**
 * Dokumenttypen für Dateinamen
 */
export type FilenameDocumentType = 
  | 'Analysebericht'
  | 'Pruefbericht'  // Legacy-Support
  | 'Handlungsempfehlungen' 
  | 'Allergenzusatzstoffliste' 
  | 'Rechnung' 
  | 'Ueberarbeitete_Karte'
  | 'Beleg';

/**
 * Mapping von Dokumenttypen zu ID-Präfixen
 */
const documentTypeToIdPrefix: Record<FilenameDocumentType, DocumentIdType> = {
  'Analysebericht': 'AB',
  'Pruefbericht': 'AB',  // Legacy → neues Kürzel
  'Handlungsempfehlungen': 'HE',
  'Allergenzusatzstoffliste': 'AZL',
  'Rechnung': 'RE',
  'Ueberarbeitete_Karte': 'UK',
  'Beleg': 'BL'
};

/**
 * NEUE zentrale Funktion zur Generierung einheitlicher Dateinamen
 * Format: [Dokumenttyp]_[ID].[Endung]
 * Beispiel: Analysebericht_AB-00123-0001-25-M.pdf
 * 
 * @param documentType - Dokumenttyp (Pruefbericht, Handlungsempfehlungen, etc.)
 * @param userNumber - User-Nummer aus profiles.user_number
 * @param scanNumber - Scan-Nummer aus user_artikelscans.scan_number
 * @param packageCode - Paket-Code (S, M, L, XL)
 * @param extension - Dateiendung (default: 'pdf')
 */
export function generateDocumentFilename(
  documentType: FilenameDocumentType,
  userNumber: number | null | undefined,
  scanNumber: number | null | undefined,
  packageCode?: string | null,
  extension: string = 'pdf'
): string {
  const idPrefix = documentTypeToIdPrefix[documentType];
  const documentId = generateDocumentId(idPrefix, userNumber, scanNumber, packageCode);
  
  return `${documentType}_${documentId}.${extension}`;
}
